import jsep, { Expression } from 'jsep';
import { preprocess } from './bracketRefs';

const ALLOWED_FUNCTIONS = [
  'isexist',
  'datatable',
  'isempty',
  'empty',
  'isexistany',
  'isnotempty',
  'compare',
  'nearmatch',
  'checkall',
  'checklength',
  'iscontains',
  'format',
  'today',
  'datedif',
  'if',
  'in',
  'ifs',
];
interface IdentifierNode extends Expression {
  type: 'Identifier';
  name: string;
}

interface CallExpressionNode extends Expression {
  type: 'CallExpression';
  callee: Expression;
  arguments: Expression[];
}

export const IsValidRule = (formula: string): boolean => {
  formula = formula.toLowerCase();
  if (!formula?.trim()) return false;

  try {
    const { cleaned, fieldMap } = preprocess(formula);
    const ast = jsep(cleaned);

    let remaining = '';
    if (typeof (ast as Expression).end === 'number') {
      const end = (ast as Expression).end;
      if (typeof end === 'number') {
        remaining = cleaned.substring(end);
        if (remaining.trim().length > 0) {
          throw new Error('Unexpected characters at the end of the formula.');
        }
      }
    }

    const validateAst = (node: Expression, isCallee = false): void => {
      if (!node) return;

      if (node.type === 'Compound') {
        throw new Error(
          'Multiple expressions without operators are not allowed.',
        );
      }

      if (node.type === 'Identifier') {
        if (isCallee) return;
        const nameNode = node as IdentifierNode;
        if (!fieldMap[nameNode.name]) {
          throw new Error(`Field or document MUST be mentioned within [].`);
        }
      }

      if (node.type === 'CallExpression') {
        const callNode = node as CallExpressionNode;
        const callee = callNode.callee as IdentifierNode;
        const name = callee.name;
        if (typeof name !== 'string' || !ALLOWED_FUNCTIONS.includes(name)) {
          throw new Error(`Disallowed function call: '${name}'`);
        }

        const args = callNode.arguments ?? [];

        if (name === 'if' || name === 'ifs') {
          if (args.length !== 3)
            throw new Error(`'${name}' requires exactly 3 parameters.`);
        } else if (
          name === 'in' ||
          name === 'compare' ||
          name === 'nearmatch'
        ) {
          if (args.length < 2)
            throw new Error(`'${name}' requires at least 2 parameters.`);
        } else if (
          name === 'empty' ||
          name === 'isempty' ||
          name === 'isnotempty'
        ) {
          if (args.length !== 1)
            throw new Error(`'${name}' requires exactly 1 parameter.`);
        } else if (name === 'checkall') {
          if (args.length !== 1)
            throw new Error(`'${name}' requires exactly 1 parameter.`);

          const arg = args[0];
          if (arg.type !== 'Identifier') {
            throw new Error(`'${name}' parameter must be a field reference.`);
          }
          const rawField = fieldMap[(arg as IdentifierNode).name];
          if (rawField) {
            const inner = rawField.replace(/^\[|\]$/g, '');
            // checkall accepts a bare field ([Last Name]) or a doc-agnostic
            // version-qualified field ([:Final.Last Name] / [*:Final.Last Name]).
            // A document-qualified form ([Paystub.Last Name]) is not allowed.
            const isVersionQualified = /^\*?:/.test(inner);
            if (inner.includes('.') && !isVersionQualified) {
              throw new Error(
                `'${name}' parameter must be in the format [fieldType] or [:version.fieldType].`,
              );
            }
          }
        } else if (name === 'checklength') {
          if (args.length !== 1)
            throw new Error(`'${name}' requires exactly 1 parameter.`);
          if (args[0].type !== 'BinaryExpression') {
            throw new Error(
              `'${name}' parameter must include a comparison operator (e.g., [field] > 6).`,
            );
          }
        } else if (name === 'isexist' || name === 'isexistany') {
          if (args.length < 1)
            throw new Error(`'${name}' requires at least 1 parameter.`);
        } else if (name === 'datedif') {
          if (args.length !== 2)
            throw new Error(`'${name}' requires exactly 2 parameters.`);

          args.forEach((arg) => {
            if (arg.type === 'Identifier') {
              const rawField = fieldMap[(arg as IdentifierNode).name];
              if (rawField && !rawField.includes('.')) {
                throw new Error(
                  `'${name}' parameter must be in the format [documentType.fieldType].`,
                );
              }
              return;
            }

            if (arg.type === 'CallExpression') {
              const callee = (arg as CallExpressionNode)
                .callee as IdentifierNode;
              if (
                callee?.name === 'today' &&
                ((arg as CallExpressionNode).arguments ?? []).length === 0
              ) {
                return;
              }
            }

            if (
              arg.type === 'Literal' &&
              typeof (arg as unknown as { value: unknown }).value === 'string'
            ) {
              return;
            }

            throw new Error(
              `'${name}' parameter must be a [documentType.fieldType] reference, today(), or a date string.`,
            );
          });
        }

        args.forEach((arg: Expression) => validateAst(arg, false));
        return;
      }

      // Recursively validate child nodes
      const nodeRecord = node as Record<string, Expression | Expression[]>;
      for (const key in nodeRecord) {
        if (key === 'callee') {
          validateAst(nodeRecord.callee as Expression, true);
          continue;
        }
        const child = nodeRecord[key];
        if (Array.isArray(child)) {
          child.forEach((c) => validateAst(c as Expression, false));
        } else if (typeof child === 'object' && child !== null) {
          validateAst(child as Expression, false);
        }
      }
    };

    validateAst(ast);

    return true;
  } catch {
    return false;
  }
};
